import { useEffect } from 'react';

import { useIntl } from '@mfw/components';

import { setSessionUnrenewableHandler } from '../../api/axios';
import { useSnackbar } from '../../context/SnackbarProvider';
import messages from './messages';

/**
 * Gives a dead session a voice.
 *
 * When the cookie can no longer be renewed — a revoked refresh token, a backend that stays
 * down — every request is refused at the barrier, and that rejection is not an API error:
 * no toast predicate matches it, and the bounded fix deliberately signs nobody out. Without
 * this the page simply stops filling in and stays that way.
 *
 * Deliberately NOT a sign-out. "Unrenewable" and "the backend is having a bad ten minutes"
 * arrive through the same door, and an outage that logs every user out is a worse failure
 * than an outage. The copy is written for that door: it asserts neither an expiry nor a
 * login screen that is not coming. The guard calls this once per unhealthy stretch — from
 * the first hard failure until a renewal succeeds again — so a wake-up burst of ten refused
 * requests is one message, and a ten-minute outage is one message too.
 */
export function useSessionExpiredNotice() {
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  useEffect(() => {
    setSessionUnrenewableHandler(() => {
      enqueueSnackbar({
        message: formatMessage(messages.sessionUnrenewable),
        variant: 'error',
      });
    });
    return () => setSessionUnrenewableHandler(null);
  }, [enqueueSnackbar, formatMessage]);
}
